import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { InboxNotification } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * The in-app inbox behind the bell (`/notifications/inbox*`).
 *
 * Two loops, deliberately separate:
 *  - the UNREAD COUNT is polled while a tab is open, with the shape copied from
 *    `use-task-reminders.ts` / `use-enquiry-summary.tsx` — a chained, jittered
 *    `setTimeout`, exponential backoff on failure, one `AbortController` per
 *    load, and a reload when a tab that has been hidden comes back;
 *  - the LIST is fetched only while the panel shows it (`listActive`), so a
 *    closed bell costs one small request a minute or two and nothing more.
 *
 * ⚠️ **An older server answers 404 for `/notifications/*`.** That reads as
 * "no inbox here": `available` turns `false`, polling stops, and the caller
 * hides the tab. Nothing is shown for it.
 *
 * ⚠️ **During a support session nothing is fetched and nothing is marked.**
 * The operator is reading somebody else's account; opening a row would mark
 * the target's notification read under them.
 *
 * Order in the panel: unread first, then newest. The list is two reads merged
 * (the unread page and the newest page) so an unread item older than the
 * newest page still leads; the unread read's `meta.total` also corrects the
 * badge.
 */

const POLL_MIN_MS = 60_000;
const POLL_MAX_MS = 120_000;
const MAX_BACKOFF_MS = 600_000;
const REFRESH_AFTER_HIDDEN_MS = 60_000;
const LIST_PER_PAGE = 30;

function isMissingEndpoint(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 404;
}

function messageOf(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  if (caught instanceof Error && caught.message) return caught.message;
  return 'Request failed';
}

/** Unread first, then newest; ties broken by id so the order is stable. */
function ordered(items: InboxNotification[]): InboxNotification[] {
  return [...items].sort((a, b) => {
    const unreadA = a.readAt === null ? 0 : 1;
    const unreadB = b.readAt === null ? 0 : 1;
    if (unreadA !== unreadB) return unreadA - unreadB;
    const byDate = b.createdAt.localeCompare(a.createdAt);
    return byDate !== 0 ? byDate : b.id - a.id;
  });
}

export interface NotificationInboxValue {
  /** `false` when the server has no inbox (404) or in a support session; `null` until known. */
  available: boolean | null;
  /** Unread notifications; `null` until the first count arrives. */
  unreadCount: number | null;
  /** The list, unread first then newest; `null` until loaded once. */
  items: InboxNotification[] | null;
  /** True while the panel waits for its first list (no list and no error yet). */
  loading: boolean;
  /** The last list failure, or `null`. */
  error: string | null;
  markRead: (id: number) => void;
  markAllRead: () => void;
  refresh: () => void;
}

interface ListState {
  identity: string;
  items: InboxNotification[] | null;
  error: string | null;
}

export function useNotificationInbox({ listActive }: { listActive: boolean }): NotificationInboxValue {
  const { user, supportSession } = useAuth();
  const userId = user?.id ?? null;
  const inSupport = supportSession !== null;
  const identity = userId === null || inSupport ? null : String(userId);

  /*
   * Everything is tagged with the identity it was loaded for, so a different
   * person (sign-out, sign-in, support start or end) starts clean without a
   * reset effect.
   */
  const [countState, setCountState] = useState<{ identity: string; count: number } | null>(null);
  const [missingFor, setMissingFor] = useState<string | null>(null);
  const [listState, setListState] = useState<ListState | null>(null);
  const [nonce, setNonce] = useState(0);

  const missing = identity !== null && missingFor === identity;
  const available: boolean | null =
    identity === null ? false : missing ? false : countState?.identity === identity ? true : null;
  const unreadCount = countState && countState.identity === identity ? countState.count : null;
  const list = listState && listState.identity === identity ? listState : null;

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  // The count loop.
  useEffect(() => {
    if (identity === null || missing) return;
    const loadedFor = identity;

    let disposed = false;
    let inflight: AbortController | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let lastLoadAt = 0;
    let failures = 0;

    const queueNextPoll = () => {
      clearTimeout(pollTimer);
      const base = POLL_MIN_MS + Math.random() * (POLL_MAX_MS - POLL_MIN_MS);
      const delay =
        failures === 0 ? base : Math.min(base * 2 ** Math.min(failures, 3), MAX_BACKOFF_MS);
      pollTimer = setTimeout(load, delay);
    };

    function load() {
      inflight?.abort();
      const controller = new AbortController();
      inflight = controller;
      lastLoadAt = Date.now();
      let stop = false;

      api
        .getInboxUnreadCount(controller.signal)
        .then((response) => {
          if (disposed || controller.signal.aborted) return;
          failures = 0;
          setCountState({ identity: loadedFor, count: response.data.count });
        })
        .catch((caught: unknown) => {
          if (disposed || controller.signal.aborted) return;
          if (isMissingEndpoint(caught)) {
            stop = true;
            setMissingFor(loadedFor);
            return;
          }
          // The last good count stays on the badge.
          failures += 1;
        })
        .finally(() => {
          if (disposed || controller.signal.aborted || stop) return;
          queueNextPoll();
        });
    }

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadAt < REFRESH_AFTER_HIDDEN_MS) return;
      load();
    };

    load();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(pollTimer);
      inflight?.abort();
    };
  }, [identity, missing, nonce]);

  // The list, only while the panel shows it.
  useEffect(() => {
    if (identity === null || missing || !listActive) return;
    const loadedFor = identity;
    const controller = new AbortController();

    // No "loading" write here: the last good list stays on screen while it
    // refreshes, and a first load reads as loading because `items` is null.
    Promise.all([
      api.listInbox({ unread: true, perPage: LIST_PER_PAGE }, controller.signal),
      api.listInbox({ perPage: LIST_PER_PAGE }, controller.signal),
    ])
      .then(([unread, newest]) => {
        if (controller.signal.aborted) return;
        const byId = new Map<number, InboxNotification>();
        for (const item of [...unread.data, ...newest.data]) byId.set(item.id, item);
        setListState({
          identity: loadedFor,
          items: ordered([...byId.values()]),
          error: null,
        });
        setCountState({ identity: loadedFor, count: unread.meta.total });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (isMissingEndpoint(caught)) {
          setMissingFor(loadedFor);
          return;
        }
        setListState((current) => ({
          identity: loadedFor,
          items: current && current.identity === loadedFor ? current.items : null,
          error: messageOf(caught),
        }));
      });

    return () => controller.abort();
  }, [identity, missing, listActive, nonce]);

  const markRead = useCallback(
    (id: number) => {
      if (identity === null) return;
      const target = identity;
      const row = list?.items?.find((item) => item.id === id);
      // Already read: nothing to change, nothing to send.
      if (row && row.readAt !== null) return;
      const now = new Date().toISOString();
      setListState((current) => {
        if (!current || current.identity !== target || !current.items) return current;
        return {
          ...current,
          items: current.items.map((item) =>
            item.id === id && item.readAt === null ? { ...item, readAt: now } : item,
          ),
        };
      });
      setCountState((current) =>
        current && current.identity === target
          ? { ...current, count: Math.max(0, current.count - 1) }
          : current,
      );
      api.markInboxRead([id]).catch(() => refresh());
    },
    [identity, list, refresh],
  );

  const markAllRead = useCallback(() => {
    if (identity === null) return;
    const target = identity;
    const now = new Date().toISOString();
    setListState((current) => {
      if (!current || current.identity !== target || !current.items) return current;
      return {
        ...current,
        items: current.items.map((item) => (item.readAt === null ? { ...item, readAt: now } : item)),
      };
    });
    setCountState((current) =>
      current && current.identity === target ? { ...current, count: 0 } : current,
    );
    api.markInboxAllRead().catch(() => refresh());
  }, [identity, refresh]);

  return useMemo<NotificationInboxValue>(
    () => ({
      available,
      unreadCount,
      items: list?.items ?? null,
      loading: listActive && available !== false && !list?.items && !list?.error,
      error: list?.error ?? null,
      markRead,
      markAllRead,
      refresh,
    }),
    [available, unreadCount, list, listActive, markRead, markAllRead, refresh],
  );
}
